import { ReactNode } from 'react';
import { GrayText } from '@/components/DesignLibrary/Widget/UserWorkflowActivity/Events/ColoredText';
import { ACTIVITY_ACTIONS, ACTIVITY_TYPES, ACTIVITY_TYPES_AND_ACTIONS_ROLLUP, ActivityNoteAddedData } from '@/typings/api-models';
import * as ApiModels from '@/typings/api-models';
import Users from '@components/Icons/Users';
import Calendar from '@components/Icons/Calendar';
import * as format from '@format';
import Buttons from '@/components/Buttons';
import s from './UserWorkflowActivityThread.module.scss';

const isObject = (details: unknown): details is Record<string, string> => Boolean(
  details
  && typeof details === 'object',
);

const getHumanReadableItem = (action: ACTIVITY_ACTIONS, type: ACTIVITY_TYPES | null, details: unknown): ReactNode => {
  if (type === ACTIVITY_TYPES.STATUS_CHANGE) {
    return <GrayText>N/A</GrayText>;
  }
  if (type === ACTIVITY_TYPES.MESSAGE) {
    return <GrayText>N/A</GrayText>;
  }
  if (type === ACTIVITY_TYPES.DOCUMENT_REQUEST && action === 'uploaded-document' && isObject(details)) {
    return <>{details.fileName}</>;
  }
  if (type === ACTIVITY_TYPES.DOCUMENT_REQUEST && action !== 'uploaded-document' && isObject(details)) {
    return <>{details.title}</>;
  }
  if (type === ACTIVITY_TYPES.TERMS && isObject(details)) {
    return <>{details.documentName}</>;
  }
  if (type === ACTIVITY_TYPES.FORM_REQUEST && isObject(details)) {
    return <>{details.name}</>;
  }
  if (type === ACTIVITY_TYPES.PAYMENT && isObject(details)) {
    return <>{details.title}</>;
  }
  if (type === ACTIVITY_TYPES.CREDIT_REPORT) {
    return <>Credit Report</>;
  }

  return <></>;
};

const getHumanReadableItemFromRollUp = (rollup: ACTIVITY_TYPES_AND_ACTIONS_ROLLUP, details: unknown): ReactNode => {
  if (
    rollup === ACTIVITY_TYPES_AND_ACTIONS_ROLLUP.USER_WORKFLOW_INITIATED
    || rollup === ACTIVITY_TYPES_AND_ACTIONS_ROLLUP.USER_WORKFLOW_STATUS_CHANGED
  ) {
    return <GrayText>N/A</GrayText>;
  }
  if (rollup === ACTIVITY_TYPES_AND_ACTIONS_ROLLUP.COMMENT_LEFT) {
    return <GrayText>N/A</GrayText>;
  }
  if (rollup === ACTIVITY_TYPES_AND_ACTIONS_ROLLUP.DOCUMENT_UPLOADED && isObject(details)) {
    return <>{details.fileName}</>;
  }
  if (
    (
      rollup === ACTIVITY_TYPES_AND_ACTIONS_ROLLUP.DOCUMENT_REQUEST_COMPLETED
      || rollup === ACTIVITY_TYPES_AND_ACTIONS_ROLLUP.DOCUMENT_REQUEST_SUBMITTED_FOR_REVIEW
    )
    && isObject(details)
  ) {
    return <>{details.title}</>;
  }
  if (rollup === ACTIVITY_TYPES_AND_ACTIONS_ROLLUP.TERMS_AGREED && isObject(details)) {
    return <>{details.documentName}</>;
  }
  if (
    (
      rollup === ACTIVITY_TYPES_AND_ACTIONS_ROLLUP.PAYMENT_INITIATED
      || rollup === ACTIVITY_TYPES_AND_ACTIONS_ROLLUP.PAYMENT_SUCCEEDED
      || rollup === ACTIVITY_TYPES_AND_ACTIONS_ROLLUP.PAYMENT_FAILED
    )
    && isObject(details)
  ) {
    return <>{details.title}</>;
  }
  if (rollup === ACTIVITY_TYPES_AND_ACTIONS_ROLLUP.FORM_REQUEST_COMPLETED && isObject(details)) {
    return <>{details.name}</>;
  }

  return <GrayText>N/A</GrayText>;
};

type Props = {
  item: Pick<ApiModels.UserWorkflowActivity, 'type' | 'action' | 'activityType'>;
  details: unknown;
  onClick: () => unknown;
};

const ItemColumn: React.FC<Props> = ({ details, item, onClick }) => {
  if (item.type === ACTIVITY_TYPES.CASE_NOTE) {
    return (
      <div style={{ width: '100%' }}>
        <p className="caption">{(details as ActivityNoteAddedData).text}</p>
        <div>
          {(details as ActivityNoteAddedData).metadata?.contactType && (
            <div className={s.metadata}>
              <Users size={16} />
              <p className="caption">Contacted via {(details as ActivityNoteAddedData).metadata?.contactType}</p>
            </div>
          )}
          {(details as ActivityNoteAddedData).metadata?.time && (details as ActivityNoteAddedData).metadata?.date && (
            <div className={s.metadata}>
              <Calendar size={16} />
              <p className="caption">
                {format.datetimeLocalTZ.abbreviated((details as ActivityNoteAddedData).metadata?.date as string)},{' '}
                {(details as ActivityNoteAddedData).metadata?.time}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      {item.type === ACTIVITY_TYPES.FORM_REQUEST
        ? <Buttons
          isWrapper
          style={{ color: 'var(--color-primary)' }}
          onClick={onClick}
        >
          <p className="caption">
            {getHumanReadableItem(item.action, item.type, details)
                || getHumanReadableItemFromRollUp(item.activityType, details)}
          </p>
        </Buttons>
        : <p className="caption">
          {getHumanReadableItem(item.action, item.type, details)
              || getHumanReadableItemFromRollUp(item.activityType, details)}
        </p>}
    </>
  );
};

export default ItemColumn;